import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ExclamationTriangleIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  BuildingOffice2Icon,
  EnvelopeIcon,
  DocumentTextIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { cn } from '@/utils/cn';
import { vendorService, Vendor, VendorStats } from '../services/vendor.service';

// Static color configurations - defined outside component to avoid recreation
const STATUS_COLORS = {
  success: 'bg-success-100 text-success-800 dark:bg-success-900/20 dark:text-success-300',
  warning: 'bg-warning-100 text-warning-800 dark:bg-warning-900/20 dark:text-warning-300',
  danger: 'bg-danger-100 text-danger-800 dark:bg-danger-900/20 dark:text-danger-300',
  secondary: 'bg-secondary-100 text-secondary-800 dark:bg-secondary-800 dark:text-secondary-300',
} as const;

const RISK_TIER_COLORS = {
  success: 'bg-success-100 text-success-800 border-success-200 dark:bg-success-900/20 dark:text-success-300 dark:border-success-800',
  warning: 'bg-warning-100 text-warning-800 border-warning-200 dark:bg-warning-900/20 dark:text-warning-300 dark:border-warning-800',
  danger: 'bg-danger-100 text-danger-800 border-danger-200 dark:bg-danger-900/20 dark:text-danger-300 dark:border-danger-800',
  secondary: 'bg-secondary-100 text-secondary-800 border-secondary-200 dark:bg-secondary-900/20 dark:text-secondary-300 dark:border-secondary-800',
} as const;

// Animation variants - defined outside component to avoid recreation
const CONTAINER_VARIANTS = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.05,
    },
  },
};

const ITEM_VARIANTS = {
  hidden: { opacity: 0, y: 10 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.2, ease: 'easeOut' },
  },
};

function VendorCard({ vendor }: { vendor: Vendor }) {
  const statusConfig = vendorService.getVendorStatusConfig(vendor.status);
  const riskTierConfig = vendorService.getRiskTierConfig(vendor.riskTier);

  const primaryContact = vendor.contacts?.find((c) => c.isPrimary) ?? vendor.contacts?.[0];

  return (
    <Link to={`/legal/vendors/${vendor.id}`} className="block">
      <Card hover="default" padding="none" className="transition-all duration-200 cursor-pointer">
        <CardContent className="p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center space-x-3 mb-2">
                <h3 className="font-semibold text-lg text-secondary-900 dark:text-white truncate">
                  {vendor.companyLegalName}
                </h3>
                <span className="text-sm font-mono text-secondary-500 dark:text-secondary-400">
                  {vendor.vendorNumber}
                </span>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 mb-4">
            <span className={cn('px-3 py-1 text-xs font-medium rounded-full', STATUS_COLORS[statusConfig.color as keyof typeof STATUS_COLORS])}>
              {statusConfig.label}
            </span>
            <span className={cn('px-3 py-1 text-xs font-medium rounded-full border', RISK_TIER_COLORS[riskTierConfig.color as keyof typeof RISK_TIER_COLORS])}>
              <ShieldCheckIcon className="w-3 h-3 inline mr-1" />
              {riskTierConfig.label} Risk
            </span>
            <span className="px-3 py-1 text-xs font-medium bg-secondary-100 text-secondary-700 dark:bg-secondary-800 dark:text-secondary-300 rounded-full">
              {vendorService.getVendorTypeLabel(vendor.vendorType)}
            </span>
          </div>

          <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
            {primaryContact && (
              <>
                <div className="flex items-center space-x-2">
                  <BuildingOffice2Icon className="w-4 h-4 text-secondary-600 dark:text-secondary-400" />
                  <span className="text-secondary-600 dark:text-secondary-400">Contact:</span>
                  <span className="font-medium text-secondary-900 dark:text-white truncate">
                    {primaryContact.fullName}
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <EnvelopeIcon className="w-4 h-4 text-secondary-600 dark:text-secondary-400" />
                  <span className="text-secondary-600 dark:text-secondary-400">Email:</span>
                  <span className="font-medium text-secondary-900 dark:text-white truncate">
                    {primaryContact.email}
                  </span>
                </div>
              </>
            )}
            <div className="flex items-center space-x-2">
              <DocumentTextIcon className="w-4 h-4 text-secondary-600 dark:text-secondary-400" />
              <span className="text-secondary-600 dark:text-secondary-400">Contracts:</span>
              <span className="font-medium text-secondary-900 dark:text-white">
                {vendor._count?.contracts ?? 0}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}

export default function VendorsList() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [selectedVendorType, setSelectedVendorType] = useState<string>('all');
  const [selectedRiskTier, setSelectedRiskTier] = useState<string>('all');
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [stats, setStats] = useState<VendorStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalVendors, setTotalVendors] = useState(0);

  const fetchVendors = useCallback(async () => {
    try {
      setLoading(true);
      const [vendorsData, statsData] = await Promise.all([
        vendorService.getVendors({
          page: currentPage,
          limit: 20,
          status: selectedStatus !== 'all' ? selectedStatus : undefined,
          vendorType: selectedVendorType !== 'all' ? selectedVendorType : undefined,
          riskTier: selectedRiskTier !== 'all' ? selectedRiskTier : undefined,
          search: searchQuery || undefined,
          sortBy: 'updatedAt',
          sortOrder: 'desc',
        }),
        vendorService.getVendors({ limit: 1 }).then(() => {
          // Stats endpoint - fetch separately if available
          return null;
        }).catch(() => null),
      ]);

      setVendors(vendorsData.vendors);
      setTotalPages(vendorsData.pagination.totalPages);
      setTotalVendors(vendorsData.pagination.total);
      if (statsData) {setStats(statsData);}
      setError(null);
    } catch (err) {
      console.error('Failed to fetch vendors:', err instanceof Error ? err.message : err);
      setError('Failed to load vendors');
      setVendors([]);
    } finally {
      setLoading(false);
    }
  }, [currentPage, selectedStatus, selectedVendorType, selectedRiskTier, searchQuery]);

  useEffect(() => {
    fetchVendors();
  }, [fetchVendors]);

  // Compute stats from current data if stats endpoint is not available
  const computedStats = stats ?? {
    totalVendors: totalVendors,
    totalContracts: 0,
    byStatus: vendors.reduce((acc, v) => {
      acc[v.status] = (acc[v.status] ?? 0) + 1;
      return acc;
    }, {} as Record<string, number>),
    byRiskTier: vendors.reduce((acc, v) => {
      acc[v.riskTier] = (acc[v.riskTier] ?? 0) + 1;
      return acc;
    }, {} as Record<string, number>),
  };

  const activeCount = computedStats.byStatus?.['ACTIVE'] ?? 0;
  const underReviewCount = computedStats.byStatus?.['UNDER_REVIEW'] ?? 0;
  const criticalHighRiskCount = (computedStats.byRiskTier?.['CRITICAL'] ?? 0) + (computedStats.byRiskTier?.['HIGH'] ?? 0);

  return (
    <motion.div
      variants={CONTAINER_VARIANTS}
      initial="hidden"
      animate="visible"
      className="p-4 sm:p-6 space-y-6"
    >
      {/* Header */}
      <motion.div variants={ITEM_VARIANTS}>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
          <div>
            <h1 className="text-3xl font-bold text-secondary-900 dark:text-white">
              Vendor Management
            </h1>
            <p className="text-secondary-600 dark:text-secondary-400 mt-1">
              Manage vendors, contracts, and third-party risk across your organization
            </p>
          </div>
          <Link to="/legal/vendors/create">
            <Button className="w-full sm:w-auto">
              <PlusIcon className="w-4 h-4 mr-2" />
              Add Vendor
            </Button>
          </Link>
        </div>
      </motion.div>

      {/* Stats */}
      <motion.div variants={ITEM_VARIANTS}>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-secondary-900 dark:text-white">
                {totalVendors}
              </div>
              <div className="text-sm text-secondary-600 dark:text-secondary-400">
                Total Vendors
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-success-600 dark:text-success-400">
                {activeCount}
              </div>
              <div className="text-sm text-secondary-600 dark:text-secondary-400">
                Active
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-warning-600 dark:text-warning-400">
                {underReviewCount}
              </div>
              <div className="text-sm text-secondary-600 dark:text-secondary-400">
                Under Review
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-danger-600 dark:text-danger-400">
                {criticalHighRiskCount}
              </div>
              <div className="text-sm text-secondary-600 dark:text-secondary-400">
                Critical/High Risk
              </div>
            </CardContent>
          </Card>
        </div>
      </motion.div>

      {/* Filters */}
      <motion.div variants={ITEM_VARIANTS}>
        <Card>
          <CardContent className="p-6">
            <div className="flex flex-col lg:flex-row gap-4">
              <div className="flex-1">
                <Input
                  type="text"
                  placeholder="Search vendors..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  leftIcon={<MagnifyingGlassIcon className="w-4 h-4" />}
                />
              </div>
              <div className="flex flex-col sm:flex-row gap-4 lg:w-auto">
                <select
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                  className="px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="all">All Statuses</option>
                  <option value="PROSPECT">Prospect</option>
                  <option value="UNDER_REVIEW">Under Review</option>
                  <option value="APPROVED">Approved</option>
                  <option value="ACTIVE">Active</option>
                  <option value="UNDER_REMEDIATION">Under Remediation</option>
                  <option value="SUSPENDED">Suspended</option>
                  <option value="TERMINATED">Terminated</option>
                  <option value="ARCHIVED">Archived</option>
                </select>
                <select
                  value={selectedVendorType}
                  onChange={(e) => setSelectedVendorType(e.target.value)}
                  className="px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="all">All Types</option>
                  <option value="SUPPLIER">Supplier</option>
                  <option value="CONTRACTOR">Contractor</option>
                  <option value="CONSULTANT">Consultant</option>
                  <option value="TECHNOLOGY_PROVIDER">Technology Provider</option>
                  <option value="PROFESSIONAL_SERVICES">Professional Services</option>
                  <option value="FINANCIAL_SERVICES">Financial Services</option>
                  <option value="FACILITIES">Facilities</option>
                  <option value="OTHER">Other</option>
                </select>
                <select
                  value={selectedRiskTier}
                  onChange={(e) => setSelectedRiskTier(e.target.value)}
                  className="px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="all">All Risk Tiers</option>
                  <option value="CRITICAL">Critical</option>
                  <option value="HIGH">High</option>
                  <option value="MEDIUM">Medium</option>
                  <option value="LOW">Low</option>
                </select>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Vendors List */}
      <motion.div variants={ITEM_VARIANTS}>
        {loading ? (
          <Card>
            <CardContent className="p-12 text-center">
              <div className="text-lg text-secondary-600 dark:text-secondary-400">
                Loading vendors...
              </div>
            </CardContent>
          </Card>
        ) : error ? (
          <Card>
            <CardContent className="p-12 text-center">
              <ExclamationTriangleIcon className="w-12 h-12 text-danger-400 dark:text-danger-600 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-secondary-900 dark:text-white mb-2">
                Error Loading Vendors
              </h3>
              <p className="text-secondary-600 dark:text-secondary-400 mb-4">
                {error}
              </p>
              <Button onClick={fetchVendors} variant="outline">
                Try Again
              </Button>
            </CardContent>
          </Card>
        ) : vendors.length > 0 ? (
          <div className="space-y-4">
            {vendors.map((vendor) => (
              <VendorCard key={vendor.id} vendor={vendor} />
            ))}

            {/* Pagination */}
            <div className="flex items-center justify-between pt-2">
              <span className="text-sm text-secondary-600 dark:text-secondary-400">
                Showing {vendors.length} of {totalVendors} vendors
              </span>
              {totalPages > 1 && (
                <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage <= 1}
                  >
                    Previous
                  </Button>
                  <span className="text-sm text-secondary-600 dark:text-secondary-400">
                    Page {currentPage} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage >= totalPages}
                  >
                    Next
                  </Button>
                </div>
              )}
            </div>
          </div>
        ) : (
          <Card>
            <CardContent className="p-12 text-center">
              <ExclamationTriangleIcon className="w-12 h-12 text-secondary-400 dark:text-secondary-600 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-secondary-900 dark:text-white mb-2">
                No vendors found
              </h3>
              <p className="text-secondary-600 dark:text-secondary-400">
                No vendors match your current filters. Try adjusting your search criteria.
              </p>
            </CardContent>
          </Card>
        )}
      </motion.div>
    </motion.div>
  );
}
